import SlackInstallButton from '@/components/slack-install-button';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Info,
  Key,
  Keyboard,
  MessageSquare,
  MicIcon,
  Shield,
  Users,
} from 'lucide-react';

const features = [
  {
    icon: BrainCircuit,
    title: 'AI-Powered Leadership Coaching',
    description:
      'Practice leadership conversations with our AI coach that adapts to your style and needs.',
  },
  {
    icon: MessageSquare,
    title: 'Interactive Roleplay',
    description:
      'Engage in realistic scenarios covering performance reviews, conflict resolution, and more.',
  },
  {
    icon: Shield,
    title: 'Safe Learning Environment',
    description:
      'Practice difficult conversations without real-world consequences.',
  },
  {
    icon: Key,
    title: 'Personalized Feedback',
    description:
      'Receive instant, actionable feedback to improve your leadership skills.',
  },
  {
    icon: Bot,
    title: 'Advanced AI Technology',
    description:
      'Powered by state-of-the-art language models for natural conversations.',
  },
  {
    icon: Users,
    title: 'Team Development',
    description:
      'Help your entire team grow their leadership and communication skills.',
  },
];

const screenshots = [
  {
    url: 'https://dopmo1eihgbgm.cloudfront.net/673ccdbda4885e4ce168c622/coaching.webp',
    alt: 'AI Coach Conversation',
    description: 'Practice realistic leadership scenarios with our AI coach',
  },
  {
    url: 'https://dopmo1eihgbgm.cloudfront.net/673ccdcda4885e4ce168cb09/feedback.webp',
    alt: 'Performance Feedback',
    description:
      'Get detailed feedback on your communication style and leadership approach',
  },
  {
    url: 'https://dopmo1eihgbgm.cloudfront.net/673ccddba4885e4ce168cced/roleplay.webp',
    alt: 'Interactive Roleplay',
    description:
      'Choose from various scenarios that match your development needs',
  },
];

const aiDisclaimer = [
  'Uses advanced Large Language Models (LLMs) that may occasionally generate responses requiring additional context',
  'Processes conversations in real-time without storing chat history',
  'Designed as a practice tool to complement, not replace, human coaching',
  'Maintains privacy and confidentiality of all interactions',
  'Provides session transcripts and analysis only to the user who initiated the session',
  'Adheres to enterprise data security standards and best practices',
];

const accessInfo = {
  title: 'Pricing / Enterprise Access',
  description:
    'Hupo AI is an enterprise leadership development solution available through your organization',
  steps: [
    {
      title: 'Contact Us',
      description:
        'Email team@hupo.co to learn about pricing and implementation for your organization',
      icon: MessageSquare,
    },
    {
      title: 'Enterprise Setup',
      description:
        "Once your organization signs up, we'll configure access for your Slack workspace",
      icon: Key,
    },
    {
      title: 'User Access',
      description:
        "Your organization's admins will provide access by whitelisting approved Slack workspaces and user IDs",
      icon: Users,
    },
  ],
};

const conversationPersonas = [
  {
    id: 'sensible',
    title: 'Sensible',
    description:
      'A mix of confidence and self-doubt, stays open yet selective with feedback.',
    emoji: '🤝',
  },
  {
    id: 'passive',
    title: 'Passive',
    description:
      'Rarely speaks up, gives minimal responses, and needs active prompting to engage.',
    emoji: '🤫',
  },
  {
    id: 'anxious',
    title: 'Anxious',
    description:
      'Overthinks decisions, seeks reassurance, and struggles with criticism.',
    emoji: '😰',
  },
  {
    id: 'defensive',
    title: 'Defensive',
    description:
      'Confident and data-driven, challenges feedback and demands strong evidence before accepting change.',
    emoji: '🛡️',
  },
];

const practiceOptions = [
  {
    title: 'Text-Based Practice',
    description:
      'Engage in leadership conversations through text chat directly within Slack DMs',
    icon: Keyboard,
  },
  {
    title: 'Voice-Based Practice',
    description:
      'Practice through natural voice conversations in our web interface, launched from Slack',
    icon: MicIcon,
  },
];

export default function SlackLanding() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
              alt="Hupo AI"
              className="h-16 w-auto mb-12"
            />

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-6xl font-bold tracking-tight text-gray-900 sm:text-7xl"
            >
              Leadership Coach
              <br />
              in Your Slack
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl"
            >
              Develop exceptional leadership skills through personalized AI
              coaching, real-time practice, and actionable feedback - all within
              your Slack workspace.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-10"
            >
              <SlackInstallButton />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to develop world-class leadership skills
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Practice real-world scenarios with our AI coach and get instant
              feedback to improve your leadership abilities.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-7xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="flex flex-col"
                >
                  <div className="mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                      <feature.icon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="flex flex-auto flex-col">
                    <h3 className="text-lg font-semibold leading-8 text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="mt-1 flex flex-auto text-base leading-7 text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Practice Options Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Practice Your Way
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Choose the practice format that works best for you - text or voice
              conversations
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-4xl">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {practiceOptions.map((option) => (
                <motion.div
                  key={option.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm"
                >
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                      <option.icon className="h-6 w-6" />
                    </div>
                    <h3 className="ml-4 text-xl font-bold text-gray-900">
                      {option.title}
                    </h3>
                  </div>
                  <p className="text-gray-600">{option.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How It Works
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our Slack integration makes leadership practice simple, effective,
              and accessible directly in your direct messages
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-4xl">
            <div className="rounded-xl bg-white p-8 mb-16 shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">
                Using Hupo AI in Slack
              </h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1 bg-primary rounded-full p-2 h-8 w-8 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">
                      Starting a Conversation
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Simply send a direct message to the Hupo AI bot in Slack
                      describing your leadership challenge or topic you want to
                      practice.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1 bg-primary rounded-full p-2 h-8 w-8 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">
                      Personalized Scenario Generation
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Based on your input, our AI dynamically generates a
                      relevant scenario with a suggested framework, then asks
                      you to select an AI persona type.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1 bg-primary rounded-full p-2 h-8 w-8 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">
                      Choose Your Practice Medium
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Select between text roleplay (directly in Slack DMs) or
                      voice roleplay (clicking the provided link to open our web
                      app).
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1 bg-primary rounded-full p-2 h-8 w-8 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">
                      Feedback & History
                    </h4>
                    <p className="text-gray-600 mt-1">
                      After your session, receive detailed feedback in your
                      Slack DM. Access your session history through the web
                      dashboard link in the welcome message.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personas Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Customize Your Experience
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Practice with different conversation partners to prepare for
              various real-world scenarios
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {conversationPersonas.map((persona) => (
                <motion.div
                  key={persona.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm h-full"
                >
                  <div className="text-center mb-4">
                    <span className="text-4xl">{persona.emoji}</span>
                    <h3 className="text-xl font-bold text-gray-900 mt-2">
                      {persona.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-center">
                    {persona.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Screenshots Section */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              See Hupo AI in Action
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Get a glimpse of how our AI coach helps develop leadership skills
              through interactive conversations and feedback
            </p>
          </div>

          <div className="mt-16 mx-auto max-w-6xl">
            {screenshots.map((screenshot, index) => (
              <motion.div
                key={screenshot.alt}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative mb-16 last:mb-0"
              >
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <img
                    src={screenshot.url}
                    alt={screenshot.alt}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4 max-w-md mx-auto -mt-6 relative z-10">
                  <h3 className="font-bold text-lg text-gray-900">
                    {screenshot.alt}
                  </h3>
                  <p className="text-gray-600 mt-1">{screenshot.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Technical Details Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Slack Integration Details
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Understanding how our AI leadership coach works within your Slack
              environment
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <MessageSquare className="h-6 w-6 text-primary mr-2" />
                  Practice Model
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      <strong>Direct Messages Only:</strong> All interactions
                      with the Hupo AI bot take place exclusively in direct
                      messages, not in channels
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Start by simply messaging the bot directly about your
                      leadership challenge
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Access your session history through the web dashboard link
                      shared in the welcome message
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      For both text and voice practice, all feedback is
                      delivered in your private DM thread
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <BrainCircuit className="h-6 w-6 text-primary mr-2" />
                  Practice Features
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      <strong>Text roleplay:</strong> Have back-and-forth
                      conversations directly within Slack DMs
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      <strong>Voice roleplay:</strong> Interactive voice
                      sessions with natural speech recognition and turn
                      detection
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Multi-language support with seamless language switching
                      for international teams
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Customize your practice with different AI persona
                      behaviors (Sensible, Passive, Anxious, or Defensive)
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Info className="h-6 w-6 text-primary mr-2" />
                  Feedback Analysis
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Comprehensive analysis of your communication style and
                      leadership approach
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Full transcript of your conversation with timestamps and
                      speaker identification
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Skill assessments with specific strengths and actionable
                      improvement suggestions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Framework-based feedback aligned with leadership best
                      practices and methodologies
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Shield className="h-6 w-6 text-primary mr-2" />
                  Data & Privacy
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      All practice sessions are private and only visible to you
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Voice recordings are processed in real-time and not stored
                      permanently
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      Enterprise controls allow workspace admins to configure
                      access permissions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-bold mr-2">•</span>
                    <span>
                      All communications are encrypted and comply with
                      enterprise security standards
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              {accessInfo.title}
            </h2>
            <p className="mt-6 text-lg text-gray-600">
              {accessInfo.description}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {accessInfo.steps.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <div className="mb-4">
                  <step.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="mailto:team@hupo.co"
              className="inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Contact Sales
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <p className="mt-4 text-sm text-gray-500">
              Enterprise pricing and implementation details available upon
              request
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="relative isolate">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to transform your leadership skills?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-orange-100">
                Install Hupo AI to your Slack workspace and start practicing
                leadership scenarios with our AI coach today.
              </p>
              <div className="mt-10 flex items-center justify-center">
                <SlackInstallButton />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <Info className="h-5 w-5 text-primary mr-2" />
                <h4 className="text-sm font-semibold text-gray-900">
                  AI Technology Disclosure
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiDisclaimer.map((point, idx) => (
                  <div key={idx} className="flex items-start">
                    <Shield className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-xs text-gray-600">{point}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                <a
                  href="/slack/privacy"
                  className="text-xs text-primary hover:underline"
                >
                  Learn more about our AI practices and data handling
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <div className="flex justify-center space-x-6 md:order-2">
            <a
              href="https://gethupo.com"
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Website</span>
              <svg
                className="h-6 w-6"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
            </a>
            <a
              href="/slack/privacy"
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Privacy Policy</span>
              <svg
                className="h-6 w-6"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3 3a1 1 0 011-1h16a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm2 2v14h14V5H5zm2 2h10v1H7V7zm0 3h10v1H7v-1zm0 3h10v1H7v-1zm0 3h7v1H7v-1z"
                />
              </svg>
            </a>
          </div>
          <div className="mt-8 md:order-1 md:mt-0">
            <p className="text-center text-xs leading-5 text-gray-500">
              &copy; {new Date().getFullYear()} Hupo AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
